import { Component } from '@theme/component';

/**
 * Real, courier-based delivery estimate (not a quick-commerce claim):
 * Delhi ships in 2 business days, other metros in 3, everywhere else in 5.
 * "Business days" skips Saturday/Sunday when counting forward from today.
 */
const STORAGE_KEY = 'dawntown:location';

const TIERS = {
  delhi: { label: 'Delhi', days: 2 },
  metro: { label: 'Metro', days: 3 },
  other: { label: 'Other', days: 5 },
};

const DELHI_CITIES = ['delhi', 'new delhi', 'gurugram', 'gurgaon', 'noida', 'ghaziabad', 'faridabad'];
const METRO_CITIES = ['mumbai', 'bengaluru', 'bangalore', 'hyderabad', 'ahmedabad', 'chennai', 'kolkata', 'pune'];

/**
 * @param {string} cityName
 * @returns {'delhi' | 'metro' | 'other'}
 */
function classifyCity(cityName) {
  const normalized = (cityName || '').trim().toLowerCase();
  if (!normalized) return 'other';
  if (DELHI_CITIES.some((city) => normalized.includes(city))) return 'delhi';
  if (METRO_CITIES.some((city) => normalized.includes(city))) return 'metro';
  return 'other';
}

/**
 * Adds N business days (skipping Saturday/Sunday) to a date.
 * @param {Date} startDate
 * @param {number} days
 * @returns {Date}
 */
function addBusinessDays(startDate, days) {
  const result = new Date(startDate);
  let added = 0;
  while (added < days) {
    result.setDate(result.getDate() + 1);
    const day = result.getDay();
    if (day !== 0 && day !== 6) added += 1;
  }
  return result;
}

/** @param {Date} date */
function formatDate(date) {
  return date.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
}

/**
 * @typedef {object} SavedCity
 * @property {string} name
 * @property {'delhi' | 'metro' | 'other'} tier
 *
 * @extends {Component}
 */
export class LocationBarComponent extends Component {
  /**
   * These elements are nested inside the <anchored-popover-component> child
   * (the dropdown trigger/panel), so the base Component's `ref="..."` system
   * would attribute them to that nested component instead of this one — it
   * assigns each ref to the *nearest* custom-element ancestor. Querying them
   * directly by data-attribute sidesteps that and always resolves against
   * this component's own subtree.
   * @type {HTMLElement | null}
   */
  cityLabel = null;
  /** @type {HTMLElement | null} */
  deliveryLabel = null;
  /** @type {HTMLElement | null} */
  locationError = null;
  /** @type {HTMLInputElement | null} */
  otherCityInput = null;

  /** @type {SavedCity} */
  city = { name: 'Delhi', tier: 'delhi' };

  connectedCallback() {
    super.connectedCallback();

    this.cityLabel = this.querySelector('[data-city-label]');
    this.deliveryLabel = this.querySelector('[data-delivery-label]');
    this.locationError = /** @type {HTMLElement | null} */ (this.querySelector('[data-location-error]'));
    this.otherCityInput = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-other-city-input]'));

    this.city = this.#loadSavedCity() ?? this.city;
    this.#render();
  }

  /** @returns {SavedCity | null} */
  #loadSavedCity() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed.name === 'string' && TIERS[parsed.tier]) return parsed;
      return null;
    } catch (error) {
      return null;
    }
  }

  /** @param {SavedCity} city */
  #saveCity(city) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(city));
    } catch (error) {
      // Private browsing / storage quota — fail silently, selection just won't persist.
    }
  }

  #render() {
    const { cityLabel, deliveryLabel } = this;
    if (!cityLabel || !deliveryLabel) return;

    const tier = TIERS[this.city.tier] ?? TIERS.other;
    const deliveryDate = addBusinessDays(new Date(), tier.days);

    cityLabel.textContent = this.city.name;
    deliveryLabel.textContent = `Delivery in ${tier.days} day${tier.days === 1 ? '' : 's'} · by ${formatDate(deliveryDate)}`;
  }

  /**
   * Bound via `on:click="#location-bar/selectCity?name=Mumbai"` from the manual city list,
   * and called directly (with just a data object, no event) from #reverseGeocode.
   * @param {{name?: string}} data
   * @param {Event} [event]
   */
  selectCity(data, event) {
    event?.preventDefault();
    const name = data?.name?.trim();
    if (!name) return;

    this.city = { name, tier: classifyCity(name) };
    this.#saveCity(this.city);
    this.#render();
    this.#hidePopover();
    this.#clearError();
  }

  /**
   * Bound via `on:submit="#location-bar/handleOtherCitySubmit"` on the free-text fallback form.
   * @param {Event} event
   */
  handleOtherCitySubmit(event) {
    event.preventDefault();
    const value = this.otherCityInput?.value;
    this.selectCity({ name: value });
  }

  /**
   * Bound via `on:click="#location-bar/useMyLocation"` on the "Use my current location" button.
   * Only ever called from an explicit user click — never automatically on page load.
   * @param {Event} [event]
   */
  useMyLocation(event) {
    event?.preventDefault();
    this.#clearError();

    if (!('geolocation' in navigator)) {
      this.#showError("Location isn't supported in this browser — pick your city below.");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => this.#reverseGeocode(position.coords.latitude, position.coords.longitude),
      () => this.#showError('Location permission denied — pick your city below.'),
      { timeout: 10000 }
    );
  }

  /**
   * Reverse-geocodes coordinates to a city name via BigDataCloud's free, key-less
   * client-side endpoint, then feeds the result through the same selectCity path
   * as a manual pick.
   * @param {number} latitude
   * @param {number} longitude
   */
  async #reverseGeocode(latitude, longitude) {
    try {
      const url = `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`;
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Reverse geocode failed: ${response.status}`);

      const data = await response.json();
      const cityName = data.city || data.locality || data.principalSubdivision;

      if (!cityName) {
        this.#showError("Couldn't detect your city — pick one below.");
        return;
      }

      this.selectCity({ name: cityName });
    } catch (error) {
      this.#showError("Couldn't detect your city — pick one below.");
    }
  }

  #hidePopover() {
    const popover = /** @type {(HTMLElement & { hidePopover?: () => void }) | null} */ (
      document.getElementById('location-city-popover')
    );
    popover?.hidePopover?.();
  }

  /** @param {string} message */
  #showError(message) {
    const { locationError } = this;
    if (!locationError) return;
    locationError.textContent = message;
    locationError.hidden = false;
  }

  #clearError() {
    const { locationError } = this;
    if (!locationError) return;
    locationError.hidden = true;
    locationError.textContent = '';
  }
}

if (!customElements.get('location-bar-component')) {
  customElements.define('location-bar-component', LocationBarComponent);
}
